import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

const STATUS_CLASSES = {
  completed: "bg-green-50 text-green-700 ring-green-600/20",
  completed_with_errors: "bg-amber-50 text-amber-700 ring-amber-600/20",
  failed: "bg-red-50 text-red-700 ring-red-600/20",
  processing: "bg-blue-50 text-blue-700 ring-blue-600/20",
  queued: "bg-blue-50 text-blue-700 ring-blue-600/20"
}

const BASE_STATUS_CLASSES = "inline-flex rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset"
const consumer = createConsumer()

export default class extends Controller {
  static targets = ["status", "total", "imported", "failed", "skipped", "rowText", "progressWrapper", "progressBar", "progressText", "errors"]

  connect() {
    const id = this.batchId()
    if (!id) {
      console.warn("Import progress controller missing import batch id", this.element)
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: "ImportBatchChannel", id: id },
      { received: (progress) => this.updateProgress(progress) }
    )
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
  }

  batchId() {
    return this.element.getAttribute("data-import-progress-id-value")
  }

  updateProgress(progress) {
    if (
      !progress ||
      typeof progress !== "object" ||
      Array.isArray(progress) ||
      !("status_label" in progress)
    ) {
      return
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = progress.status_label
      this.statusTarget.className = `${BASE_STATUS_CLASSES} ${STATUS_CLASSES[progress.status] || "bg-slate-100 text-slate-700 ring-slate-600/20"}`
    }

    this.setNumberTarget("total", progress.total_rows)
    this.setNumberTarget("imported", progress.imported_rows)
    this.setNumberTarget("failed", progress.failed_rows)
    this.setNumberTarget("skipped", progress.skipped_rows)

    if (this.hasRowTextTarget) {
      this.rowTextTarget.textContent = `${this.formatNumber(progress.imported_rows)} imported, ${this.formatNumber(progress.failed_rows)} failed`
    }

    this.updateProgressBar(progress)

    if (this.hasErrorsTarget) {
      this.errorsTarget.innerHTML = this.errorHtml(progress.error_log || [])
    }
  }

  updateProgressBar(progress) {
    const percentage = this.progressPercentage(progress)

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
      this.progressBarTarget.setAttribute("aria-valuenow", percentage)
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percentage}%`
    }

    this.toggleProgressBar(progress, percentage)
  }

  toggleProgressBar(progress, percentage) {
    if (!this.hasProgressWrapperTarget) return

    const activeStatus = ["queued", "processing"].includes(progress.status)
    this.progressWrapperTarget.classList.toggle("hidden", !activeStatus || percentage >= 100)
  }

  progressPercentage(progress) {
    if (typeof progress.progress_percentage === "number") {
      return Math.max(0, Math.min(100, progress.progress_percentage))
    }

    const totalRows = Number(progress.total_rows || 0)
    if (totalRows <= 0) return 0

    const processedRows = Number(progress.processed_rows || 0) ||
      Number(progress.imported_rows || 0) + Number(progress.failed_rows || 0) + Number(progress.skipped_rows || 0)

    return Math.max(0, Math.min(100, Math.round((processedRows / totalRows) * 100)))
  }

  setNumberTarget(name, value) {
    const targetName = `${name}Target`
    const hasTargetName = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    if (!this[hasTargetName]) return

    this[targetName].textContent = this.formatNumber(value)
  }

  formatNumber(value) {
    return new Intl.NumberFormat().format(value || 0)
  }

  errorHtml(errors) {
    if (errors.length === 0) {
      return `<p class="mt-4 text-sm text-slate-500">No row errors recorded.</p>`
    }

    return `<div class="mt-4 space-y-3">${errors.map((error) => `
      <div class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <p class="font-medium">${this.escape(error.sheet || "-")} row ${this.escape(error.row || "-")}</p>
        <p class="mt-1">${this.escape(error.message || "")}</p>
      </div>
    `).join("")}</div>`
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }
}
